import { z } from 'zod';
import { EMPTY_FILTERS, STAGES, type AtlasFilters, type BaseMap, type LayerId } from './atlasModel';
import type { LocationIntelligence } from './atlasApi';
const stage = z.custom<AtlasFilters['stages'][number]>((value) =>
  STAGES.includes(value as AtlasFilters['stages'][number]),
);
const viewSchema = z.object({
  id: z.string().max(80),
  name: z.string().trim().min(1).max(60),
  filters: z.object({
    search: z.string().max(200),
    stages: z.array(stage).max(11),
    sector: z.string().max(200),
    region: z.string().max(100),
    client: z.string().max(100),
    risk: z.string().max(20),
    year: z.string().max(10),
    location: z.enum(['all', 'located', 'unlocated']),
    nearby: z.boolean(),
  }),
  layers: z
    .array(
      z.enum([
        'jobs',
        'value',
        'risk',
        'clients',
        'resources',
        'demographics',
        'businesses',
        'development',
        'flood',
        'parcels',
        'access',
        'amenities',
        'suppliers',
      ]),
    )
    .max(13),
  basemap: z.enum(['dark', 'street', 'satellite', 'terrain']),
  radius: z.union([z.literal(1), z.literal(3), z.literal(5)]),
  view: z.enum(['map', 'register']),
});
export interface SavedAtlasView {
  id: string;
  name: string;
  filters: AtlasFilters;
  layers: LayerId[];
  basemap: BaseMap;
  radius: 1 | 3 | 5;
  view: 'map' | 'register';
}
export function loadViews(storageKey: string): SavedAtlasView[] {
  try {
    return z
      .array(viewSchema)
      .max(20)
      .parse(JSON.parse(localStorage.getItem(storageKey) ?? '[]'));
  } catch {
    return [];
  }
}
export function saveViews(storageKey: string, views: SavedAtlasView[]) {
  localStorage.setItem(storageKey, JSON.stringify(z.array(viewSchema).max(20).parse(views)));
}
export function preset(value: string): { filters: AtlasFilters; layers: LayerId[] } {
  const filters = { ...EMPTY_FILTERS };
  if (value === 'delivery')
    return {
      filters: { ...filters, stages: ['active', 'on_hold'] },
      layers: ['jobs', 'risk', 'resources'],
    };
  if (value === 'pipeline')
    return {
      filters: {
        ...filters,
        stages: ['pursuit', 'estimating', 'submitted_bid', 'awarded_pending'],
      },
      layers: ['jobs'],
    };
  if (value === 'history')
    return {
      filters: { ...filters, stages: ['completed', 'lost_bid', 'no_bid', 'cancelled'] },
      layers: ['jobs', 'clients'],
    };
  if (value === 'site')
    return { filters, layers: ['jobs', 'flood', 'parcels', 'access', 'suppliers'] };
  return { filters, layers: ['jobs'] };
}
export function locationBrief(
  title: string,
  data: LocationIntelligence | undefined,
  radius: number,
  jobs: number,
): string {
  return [
    '# HELIOS — Location brief',
    '',
    title,
    '',
    'Retrieved: ' + (data?.retrievedAt ?? 'No external observations'),
    'Local job radius: ' +
      radius +
      ' miles (straight-line distance). Located jobs under current filters: ' +
      jobs +
      '.',
    '',
    '## Demographics',
    data?.demographics.period ?? '',
    data?.demographics.geography ?? 'Geography unavailable',
    'Whole-tract estimates, not radius totals. Margins are 90% confidence margins of error.',
    ...(data?.demographics.metrics.map(
      (m) =>
        m.label +
        ': ' +
        (m.value ?? 'Unavailable') +
        ' ' +
        m.unit +
        (m.margin !== null ? ' ± ' + m.margin : ''),
    ) ?? []),
    '',
    '## County growth',
    data?.growth?.geography ?? 'County unavailable',
    (data?.growth?.previousPeriod ?? '') + ' → ' + (data?.growth?.currentPeriod ?? ''),
    data?.growth?.basis ?? 'Growth unavailable',
    ...(data?.growth?.metrics.map(
      (m) =>
        m.label +
        ': ' +
        (m.previous ?? 'Unavailable') +
        ' → ' +
        (m.current ?? 'Unavailable') +
        '; change ' +
        (m.change ?? 'Unavailable') +
        '; ' +
        (m.percent === null ? 'Rate unavailable' : m.percent.toFixed(1) + '%') +
        '; change margin ' +
        (m.changeMargin ?? 'Unavailable') +
        '; exceeds sampling uncertainty: ' +
        (m.distinguishable ?? 'Unknown'),
    ) ?? []),
    '',
    '## County daytime population',
    data?.daytime?.geography ?? 'County unavailable',
    data?.daytime?.period ?? '',
    data?.daytime?.method ?? 'Daytime population unavailable',
    'Residents: ' + (data?.daytime?.residents ?? 'Unavailable'),
    'Resident workers: ' + (data?.daytime?.residentWorkers ?? 'Unavailable'),
    'Workplace workers: ' + (data?.daytime?.workplaceWorkers ?? 'Unavailable'),
    'Commuter-adjusted population: ' +
      (data?.daytime?.population ?? 'Unavailable') +
      ' ± ' +
      (data?.daytime?.margin ?? 'Unavailable'),
    '',
    '## Parcel ownership and zoning',
    data?.parcels?.coverage ?? 'Parcel observations unavailable',
    data?.parcels?.truncated ? 'Showing first 25 records; additional records exist.' : '',
    ...(data?.parcels?.records.flatMap((p) => [
      'Parcel: ' + (p.parcelNumber ?? 'Unavailable'),
      'Recorded owners: ' + (p.owners.join('; ') || 'Unavailable'),
      'County-reported acres: ' + (p.acres ?? 'Unavailable'),
      'Land use: ' + (p.landUse ?? 'Unavailable'),
      'Zoning: ' +
        (p.zoning.code ?? 'Unavailable') +
        ' — ' +
        (p.zoning.description ?? 'Unavailable'),
      'Parcel source refresh: ' + (p.refreshedAt ?? 'Unavailable'),
      'Zoning data date: ' + (p.zoning.dataDate ?? 'Unavailable'),
      p.sourceUrl ?? '',
      p.zoning.ordinanceUrl ?? '',
      '',
    ]) ?? []),
    '',
    '## Business activity',
    data?.businesses.period ?? '',
    data?.businesses.geography ?? 'County unavailable',
    ...(data?.businesses.industries.map(
      (i) =>
        i.label +
        ': ' +
        (i.establishments ?? 'Unavailable') +
        ' employer establishments; ' +
        (i.employees ?? 'Unavailable') +
        ' employees',
    ) ?? []),
    '',
    '## Flood screening',
    data?.flood.coverage ?? 'Not queried',
    ...(data?.flood.zones.map((z) => 'Zone ' + z.zone + (z.subtype ? ' — ' + z.subtype : '')) ??
      []),
    'No mapped zone does not establish low flood risk. Verify effective maps and jurisdiction records.',
    '',
    '## Sources & coverage',
    ...(data?.sources.flatMap((s) => [s.name + ' — ' + s.state, s.detail, s.url, '']) ?? []),
  ].join('\n');
}
export function download(content: string, filename: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
